import { Empty, message } from 'antd';
import { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  CasesIdentityVerificationV1CaseDetailsFilesContainerIdentityVerificationCaseV1Document,
  CasesIdentityVerificationV1CaseDetailsFilesContainerIdentityVerificationCaseV1UpdateDocument,
  FileInfo,
  IdentityVerificationCaseV1UpdateInput
} from '../../../../../../../../generated';
import { ComponentQueryLoader } from '../../../../../../../shared/component-query-loader';
import { AzureBlobResponse } from '../../../../../../../shared/file-upload/azure-upload';
import { CaseDetailsFiles } from './case-details-files';

interface CaseDetailsFilesContainerProps {
  caseId: string;
}

export const CaseDetailsFilesContainer: React.FC<CaseDetailsFilesContainerProps> = (props) => {
  const [isEditingNotarizedIdForm, setIsEditingNotarizedIdForm] = useState(false);

  const [updateIdentityVerificationCaseV1, { loading: updateIdentityVerificationCaseV1Loading }] = useMutation(
    CasesIdentityVerificationV1CaseDetailsFilesContainerIdentityVerificationCaseV1UpdateDocument
  );
  const [getFilePreviewData, { loading: getFilePreviewDataLoading, data: getFilePreviewDataData, error: getFilePreviewDataError }] = useLazyQuery(
    CasesIdentityVerificationV1CaseDetailsFilesContainerIdentityVerificationCaseV1Document,
    {
      fetchPolicy: 'network-only'
    }
  );

  const [idFormInfo, setIdFormInfo] = useState<FileInfo>();
  const [uploadedData, setUploadedData] = useState<IdentityVerificationCaseV1UpdateInput>({ id: '', assets: {} });

  useEffect(() => {
    const doGetFilePreviewDataForIdForm = async () => {
      getFilePreviewData({
        variables: {
          fileName: 'id-form.pdf',
          identityVerificationCaseV1Id: props.caseId
        }
      }).then((response) => {
        if (response.data?.identityVerificationCaseV1?.fileByFileName) {
          setIdFormInfo({
            name: response.data.identityVerificationCaseV1.fileByFileName.name,
            url: response.data.identityVerificationCaseV1.fileByFileName.url,
            type: response.data.identityVerificationCaseV1.fileByFileName.type
          });
        }
      });
    };

    doGetFilePreviewDataForIdForm();
  }, []);

  const handleSave = async () => {
    if (!uploadedData) {
      message.success('Application data saved successfully');
      setIsEditingNotarizedIdForm(false);
      return;
    }
    try {
      await updateIdentityVerificationCaseV1({
        variables: {
          input: uploadedData as IdentityVerificationCaseV1UpdateInput
        }
      }).then((response) => {
        if (response.data?.identityVerificationCaseV1Update.status.success) {
          message.success('Application data saved successfully');
        } else {
          message.error('Error saving file:' + response.data?.identityVerificationCaseV1Update.status.errorMessage);
        }
      });
    } catch (error) {
      message.error('Error saving Application data');
    }
    setIsEditingNotarizedIdForm(false);
  };

  const handleSaveAndComplete = async () => {
    if (!getFilePreviewDataData?.identityVerificationCaseV1?.assets?.shared?.notarizedIdForm && !uploadedData) {
      message.error('Please upload the Notarized Id Form');
      return;
    }

    const updateInput: IdentityVerificationCaseV1UpdateInput = {
      id: props.caseId,
      assets: uploadedData?.assets,
      revisionRequest: {
        requestedChanges: {
          requestUploadNotarizedIdForm: false
        },
        revisionSubmittedAt: new Date().toISOString()
      }
    };
    try {
      await updateIdentityVerificationCaseV1({
        variables: {
          input: updateInput
        }
      }).then((response) => {
        if (response.data?.identityVerificationCaseV1Update.status.success) {
          message.success('Application data saved successfully');
          setIsEditingNotarizedIdForm(false);
        } else {
          message.error('Error saving Application data: ' + response.data?.identityVerificationCaseV1Update.status.errorMessage);
        }
      });
    } catch (error) {
      message.error('Error saving Application data');
    }
  };

  const handleUploadSuccess = async (response: AzureBlobResponse, fieldName: string) => {
    setUploadedData({
      id: props.caseId,
      assets: {
        shared: {
          [fieldName]: response.blobUrl,
          [`${fieldName}Version`]: response.versionId
        }
      }
    });
    message.success('File uploaded successfully');
  };

  if (!props.caseId) {
    return <Empty description="There was an issue loading the case details." />;
  }

  const idFormInfoData = idFormInfo ?? { name: '', url: '', type: '' };

  return (
    <ComponentQueryLoader
      error={getFilePreviewDataError}
      loading={getFilePreviewDataLoading || updateIdentityVerificationCaseV1Loading}
      hasData={!!idFormInfo?.url}
      hasDataComponent={
        <CaseDetailsFiles
          getFilePreviewDataLoading={getFilePreviewDataLoading}
          idFormInfo={idFormInfoData}
          getFilePreviewDataError={getFilePreviewDataError ?? new Error()}
          caseId={props.caseId}
          isEditingNotarizedIdForm={isEditingNotarizedIdForm}
          setIsEditingNotarizedIdForm={setIsEditingNotarizedIdForm}
          handleUploadSuccess={handleUploadSuccess}
          getFilePreviewDataData={getFilePreviewDataData}
          handleSave={handleSave}
          updateIdentityVerificationCaseV1Loading={updateIdentityVerificationCaseV1Loading}
          handleSaveAndComplete={handleSaveAndComplete}
          uploadedData={uploadedData}
        />
      }
      noDataComponent={
        <CaseDetailsFiles
          getFilePreviewDataLoading={getFilePreviewDataLoading}
          idFormInfo={idFormInfoData}
          getFilePreviewDataError={getFilePreviewDataError ?? new Error()}
          caseId={props.caseId}
          isEditingNotarizedIdForm={isEditingNotarizedIdForm}
          setIsEditingNotarizedIdForm={setIsEditingNotarizedIdForm}
          handleUploadSuccess={handleUploadSuccess}
          getFilePreviewDataData={getFilePreviewDataData}
          handleSave={handleSave}
          updateIdentityVerificationCaseV1Loading={updateIdentityVerificationCaseV1Loading}
          handleSaveAndComplete={handleSaveAndComplete}
          uploadedData={uploadedData}
        />
      }
    />
  );
};
